// ═══════════════════════════════════════════════════════════════
// WelcomeGregModal — modale first-login Greg
// S'affiche une seule fois par navigateur (localStorage), dismiss
// définitif sur « Explorer mon espace » ou « Passer ».
// Audio éditorial : pop à l'ouverture, cloche d'or à la validation.
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useRef } from 'react';
import useEditorialSound from '../../hooks/useEditorialSound';
import './WelcomeGregModal.css';

const STORAGE_KEY = 'greg_welcome_seen';

export function hasSeenGregWelcome() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function resetGregWelcome() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* silent */ }
}

export default function WelcomeGregModal({ open, onClose, onValidate }) {
  const { editorialPop, goldenBell, inkClick } = useEditorialSound();
  const openedRef = useRef(false);

  // Son d'ouverture : une seule fois par mount, déclenché au passage open true
  useEffect(() => {
    if (open && !openedRef.current) {
      openedRef.current = true;
      // Petit délai pour laisser l'animation s'enclencher
      const t = setTimeout(() => editorialPop(), 180);
      return () => clearTimeout(t);
    }
    if (!open) openedRef.current = false;
  }, [open, editorialPop]);

  // Escape ferme la modale
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleSkip();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const markSeen = () => {
    try { localStorage.setItem(STORAGE_KEY, 'true'); } catch { /* silent */ }
  };

  const handleValidate = () => {
    goldenBell();
    markSeen();
    onValidate?.();
    onClose?.();
  };

  const handleSkip = () => {
    inkClick();
    markSeen();
    onClose?.();
  };

  const handleOverlayClick = (e) => {
    // Clic en dehors de la card → skip
    if (e.target === e.currentTarget) handleSkip();
  };

  return (
    <div
      className="greg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="greg-welcome-title"
    >
      <div className="greg-modal__overlay" onClick={handleOverlayClick}>
        <div className="greg-modal__card">
          <div className="greg-modal__logo-row">
            <div className="greg-modal__logo-mark" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2L22 8V16L12 22L2 16V8L12 2Z" />
              </svg>
            </div>
            <div className="greg-modal__logo-wordmark">ITRIIA</div>
          </div>

          <div className="greg-modal__eyebrow">Bienvenue</div>
          <div id="greg-welcome-title" className="greg-modal__name">Greg.</div>

          <div className="greg-modal__avatars">
            <div className="greg-modal__avatar-group">
              <div className="greg-modal__avatar">
                <AvatarImage src="/welcome/greg/avatar-ben.svg" alt="Ben" fallback="B" />
              </div>
              <div className="greg-modal__avatar-name">Ben</div>
              <div className="greg-modal__avatar-role">Architecte</div>
            </div>
            <div className="greg-modal__avatars-divider" />
            <div className="greg-modal__avatar-group">
              <div className="greg-modal__avatar">
                <AvatarImage src="/welcome/greg/avatar-pierre.svg" alt="Pierre" fallback="P" />
              </div>
              <div className="greg-modal__avatar-name">Pierre</div>
              <div className="greg-modal__avatar-role">Fondateur</div>
            </div>
          </div>

          <blockquote className="greg-modal__quote">
            « Bienvenue Greg. Tu vas pouvoir tester, comparer, construire tes propres preuves. Ce n'est pas une démo. C'est ton poste de travail. »
          </blockquote>

          <div className="greg-modal__glyph-sep"><span>✧</span></div>

          <div className="greg-modal__signature">
            — Ben &amp; Pierre, 20 avril 2026
          </div>

          <div className="greg-modal__actions">
            <button
              type="button"
              className="greg-modal__btn greg-modal__btn--ghost"
              onClick={handleSkip}
            >
              Passer
            </button>
            <button
              type="button"
              className="greg-modal__btn greg-modal__btn--primary"
              onClick={handleValidate}
            >
              Explorer mon espace
              <ArrowRightIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sous-composants internes ────────────────────────────────

function AvatarImage({ src, alt, fallback }) {
  const [errored, setErrored] = React.useState(false);
  if (errored || !src) {
    return <span>{fallback}</span>;
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setErrored(true)}
    />
  );
}

function ArrowRightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
